import React from "react";
import { defineMessages, injectIntl } from "react-intl";
import Select from "react-select";
import styled, { withTheme } from "styled-components/native";
import { localize } from "cci-utility";
import { Container } from "../layout/common";
import Label from "./Label";
import Error from "./Error";
import Button from "../button/Button";

const messages = defineMessages({
  selectAnOption: {
    id: "selectAnOption",
    defaultMessage: "Select"
  }
});

/**
 * Wraps a react-select field to comply with how our fields work and operate.
 * A label is shown above the field and if there's a validation error, it is shown below the field.
 */
const SelectField = ({
  intl,
  theme,
  color,
  contentWidth,
  backgroundColor,
  indicatorSeparator = theme.indicatorSeparator,
  borderColor,
  clearable,
  refresh,
  letterSpacing,
  labelNumberofLines,
  labelProps,
  ...restProps
}) => {
  // Process labels as a string (if specified as such) or a react-intl message (for translation).
  const label = localize(intl, restProps.label),
    error = localize(intl, restProps.error),
    placeholder = localize(
      intl,
      restProps.placeholder || messages.selectAnOption
    ),
    addedProps = restProps.flexDirection === 'row' ? { flex: 1 } : {};

  return (
    <Container
      margins={restProps.margins}
      flex={restProps.flex}
      width={restProps.width}
      flexDirection={restProps.flexDirection}
      alignItems={restProps.alignItems}
    >
      {/* Field Label */}
      {label && (
        <Label margins="0 5px 5px 0" numberOfLines={labelNumberofLines} {...labelProps}>
          {label}
        </Label>
      )}

      <Container flexDirection="row" {...addedProps}>
        <Select
          isClearable={clearable}
          menuPlacement="auto"
          maxMenuHeight={200}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: base => ({
              ...base,
              zIndex: 9999
            }),
            input: base => ({
              ...base,
              color: theme.fieldFontColor || theme.fontColor,
              margin: 0,
              padding: 0
            }),
            placeholder: base => ({
              ...base,
              color: theme.placeholderColor || theme.fontColor
            }),
            dropdownIndicator: base => ({
              ...base,
              color: color || theme.fieldFontColor || theme.fontColor,
              // Not sure why react-select originally adds some padding to the top/bottom which does not allow us to have a smaller min-height than 38 pixels
              padding: "0px 5px",

              ":hover": {
                color: color || theme.fieldFontColor || theme.fontColor
              }
            }),
            clearIndicator: base => ({
              ...base,
              color: color || theme.fieldFontColor || theme.fontColor,
              // Not sure why react-select originally adds some padding to the top/bottom which does not allow us to have a smaller min-height than 38 pixels
              padding: "0px 8px",

              ":hover": {
                color: color || theme.fieldFontColor || theme.fontColor
              }
            }),
            indicatorSeparator: base => ({
              ...base,
              display: indicatorSeparator ? "block" : "none"
            }),
            singleValue: base => ({
              ...base,
              color: color || theme.fieldFontColor || theme.fontColor
            }),
            noOptionsMessage: base => ({
              ...base,
              fontFamily: theme.fontFamily
            }),
            option: (base, { isDisabled, isSelected, isFocused }) => ({
              ...base,
              backgroundColor: isSelected
                ? theme.secondaryColor
                : isFocused
                ? theme.secondaryColor
                : "transparent",
              fontFamily: theme.fontFamily,
              fontSize: theme.fontSize,
              color: isDisabled
                ? theme.disabledFontColor
                : isSelected || isFocused
                ? theme.selectFontColor || theme.fontColor
                : "inherit",

              ":active": {
                backgroundColor: isSelected
                  ? theme.secondaryColor
                  : theme.secondaryColor
              }
            }),

            container: base => ({
              flex: contentWidth ? null : 1,
              width:
                contentWidth && restProps.value && restProps.value.label
                  ? `${theme.letterSize * restProps.value.label.length +
                      theme.indicatorWidth}px`
                  : (contentWidth && `${theme.contentDefaultWidth}px`) ||
                    (contentWidth && "80px")
            }),

            control: base => ({
              ...base,

              borderTopWidth: theme.fieldBorderTopWidth,
              borderRightWidth: theme.fieldBorderRightWidth,
              borderBottomWidth: theme.fieldBorderBottomWidth,
              borderLeftWidth: theme.fieldBorderLeftWidth,
              borderColor: borderColor || theme.fieldBorderColor,
              backgroundColor: backgroundColor || theme.fieldBackgroundColor,
              borderRadius: theme.fieldBorderRadius,

              minHeight: theme.fieldMinHeight,
              height: theme.fieldHeight,
              fontFamily: theme.fontFamily,
              fontSize: theme.fontSize,
              color: theme.fieldFontColor || theme.fontColor,
              letterSpacing: letterSpacing || null,

              boxShadow: null,
              ":hover": {
                borderColor: borderColor || theme.fieldBorderColor
              }
            })
          }}
          {...restProps}
          placeholder={placeholder}
        />

        {/* Refresh button */}
        {refresh && (
          <Button
            ui="link"
            leftIcon="sync-alt"
            margins="0 3px 0 7px"
            onPress={() => refresh()}
          />
        )}
      </Container>

      {/* Error Message */}
      {error && <Error>{error}</Error>}
    </Container>
  );
};

export default injectIntl(withTheme(SelectField));
